import * as React from "react";
import { useSelector } from "react-redux";

import { DataSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import {
	DefaultFormModelMap,
	FormModelMap,
	useDocumentPathForInput
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon/main/icon.view";
import { Column } from "@com.mgmtp.a12.widgets/widgets-core/lib/table/new-api/column.api";
import { HintTooltip } from "@com.mgmtp.a12.widgets/widgets-core/lib/tooltip";
import { DocumentServiceFactory, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { hasAnnotation } from "../../../antragstellungs-client/utils/formModelHelper";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { assertNonNullable } from "../../../utilities/assert";

import { ModulFDataContext } from "./contexts";

const fileExtensionMap: { [key: string]: FormModel.PlaceholderIconType } = {
	doc: "text",
	docx: "text",
	odt: "text",
	rtf: "text",
	txt: "text",
	pdf: "pdf",
	jpg: "image",
	jpeg: "image",
	png: "image",
	gif: "image",
	bmp: "image",
	tiff: "image",
	webp: "image",
	mp4: "video",
	mkv: "video",
	avi: "video",
	mov: "video",
	mp3: "sound",
	wav: "sound",
	flac: "sound",
	aac: "sound",
	xls: "spreadsheet",
	xlsx: "spreadsheet",
	ods: "spreadsheet"
};

export function CustomFieldOverviewColumn(props: {
	readonly modelElement: FormModel.FieldOverviewColumn;
	readonly config: FormModelMap.RenderConfiguration;
	readonly repeat: FormModel.Repeat;
	readonly alignment?: Column.HorizontalAlignment;
}): React.ReactElement {
	const documentPath = useDocumentPathForInput(
		props.modelElement.elementPath,
		props.config.renderOptions.state.models.documentModel
	);
	const activityId = React.useContext(ActivityIdContext);
	assertNonNullable(activityId);

	const engineState = useSelector(FormEngineSelectors.engineState(activityId));
	const document = engineState && DataSelectors.document()(engineState);
	assertNonNullable(document);

	if (props.repeat === undefined) {
		return <></>;
	}

	if (hasAnnotation(props.modelElement, "mf_runtime_role", "veraktungFilename")) {
		const documentService = new DocumentServiceFactory().getDocumentService();
		const filename = documentService.getAssignedObject(document as GroupInstance, documentPath);
		const guid = documentService.getAssignedObject(document as GroupInstance, [
			...documentPath.slice(undefined, -1),
			{ elementName: "uuid", index: 1 }
		]);

		return (
			<div style={{ display: "flex", width: "100%", alignItems: "center" }}>
				<ModulFDataContext.Provider value={documentPath}>
					<>{((filename ?? "") as string).substring(guid ? (guid as string).length + 1 : 0)}</>
				</ModulFDataContext.Provider>
				<HintTooltip text={(filename as string) ?? "?"} />
			</div>
		);
	}

	if (hasAnnotation(props.modelElement, "mf_runtime_role", "veraktungFormat")) {
		const documentService = new DocumentServiceFactory().getDocumentService();
		const fileExtension = documentService.getAssignedObject(document as GroupInstance, documentPath);
		if (!fileExtension) {
			return <></>;
		}

		const icon = `datatype_${fileExtensionMap[fileExtension as string] ?? "default"}`;
		return (
			<Icon iconTheme="custom" size="big" title={fileExtension as string}>
				{icon}
			</Icon>
		);
	}

	return (
		<ModulFDataContext.Provider value={documentPath}>
			<DefaultFormModelMap.FieldOverviewColumn.component {...props} />
		</ModulFDataContext.Provider>
	);
}
